import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
import { toQueryParams, type MerchantFilters } from './merchantFilters'

export type Merchant = components['schemas']['MerchantResponse']
export type MerchantCreate = components['schemas']['MerchantCreate']
export type MerchantUpdate = components['schemas']['MerchantUpdate']

/** One page, in the shape the screen needs rather than the wire's. */
export type MerchantPage = {
  merchants: Merchant[]
  total: number
  hasMore: boolean
  page: number
  /* The size the SERVER used, not the one this client asked for. They are the
     same today, but the pager's "Showing 51–100" arithmetic is only correct
     against the size the rows were actually cut at — reading it back means a
     server that clamps or ignores the request cannot silently shift the range
     by a page. */
  pageSize: number
}

/**
 * Carries the HTTP `status`, not just the parsed body.
 *
 * `openapi-fetch`'s `error` is the response BODY, and the API's body is
 * FastAPI's bare `{"detail": "..."}` with no status field — the status lives
 * on the sibling `response`. Task 7 needs to tell a 409 apart from a 422, so
 * it is captured here, at the one place every read and write throws.
 */
export class MerchantsError extends Error {
  readonly body: unknown
  readonly status: number

  constructor(body: unknown, status: number) {
    super(`Merchants request failed with ${status}`)
    this.name = 'MerchantsError'
    this.body = body
    this.status = status
  }
}

async function listMerchants(filters: MerchantFilters): Promise<MerchantPage> {
  const { data, error, response } = await api.GET('/api/v1/merchants', {
    params: { query: toQueryParams(filters) },
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return {
    merchants: data.merchants,
    total: data.total,
    hasMore: data.has_more ?? false,
    page: data.page ?? filters.page,
    pageSize: data.page_size ?? filters.pageSize,
  }
}

/**
 * Four states, not five — see the `merchants` row in the surface registry.
 *
 * `keepPreviousData` because paging and typing in the search box both change
 * the key: without it every keystroke drops the grid to its loading skeleton
 * and the page visibly flashes. With it the previous page stays on screen,
 * dimmed by the screen's own `isFetching` treatment, until the next arrives.
 */
export function useMerchants(filters: MerchantFilters): AsyncState<MerchantPage> {
  const query = useQuery({
    queryKey: keys.merchants.list(toQueryParams(filters)),
    queryFn: () => listMerchants(filters),
    placeholderData: keepPreviousData,
  })
  return fromQuery(query, {
    message: () => 'Merchants could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (page) => page.total === 0,
  })
}

/** The one query-shape `useOwnedMerchantsTotal` ever sends — its own key,
 *  distinct from anything `toQueryParams` ever builds (that helper always
 *  sends `PAGE_SIZE` (50) and never sends `include_global` at all), so this
 *  never shares a cache entry with the main list. */
const OWNED_TOTAL_PARAMS = { page: 1, page_size: 1, include_global: false } as const

async function getOwnedMerchantsTotal(): Promise<number> {
  const { data, error, response } = await api.GET('/api/v1/merchants', {
    params: { query: OWNED_TOTAL_PARAMS },
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return data.total
}

/**
 * The user/household total, for the header's ownership-split subtitle —
 * "N system · N added by you" (`Merchants.tsx`). The main list's own `total`
 * is everything (global merchants included by default); this is the other
 * half of that split, `include_global=false` against the same endpoint.
 *
 * `page_size: 1` — the count lives on `total`, not on the page of rows
 * itself, so a real page is never fetched just to be thrown away. Paging the
 * whole list to count it would scale with the list rather than with the
 * one number this needs.
 *
 * A plain `useQuery`, not `AsyncState`/`<Async>` — this backs a few words of
 * secondary text next to the page title, not a surface of its own with a
 * skeleton and an empty state to design for. `total` is `undefined` while
 * loading or on error; the caller falls back to the plain merchant count
 * either way, the same "not measured yet" degrade `useMerchants`'s own
 * success branch already carries for its total until the first response.
 */
export function useOwnedMerchantsTotal(): { total: number | undefined; isError: boolean } {
  const query = useQuery({
    queryKey: keys.merchants.list(OWNED_TOTAL_PARAMS),
    queryFn: getOwnedMerchantsTotal,
  })
  return { total: query.data, isError: query.isError }
}

async function getMerchant(id: string): Promise<Merchant> {
  const { data, error, response } = await api.GET('/api/v1/merchants/{merchant_id}', {
    params: { path: { merchant_id: id } },
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return data
}

/**
 * `/merchants/:id/transactions` — originally Task 11's `/merchants/:id`,
 * repointed at `MerchantTransactions.tsx` once the design that arrived
 * 2026-08-21 replaced the detail SCREEN with an expanded row (see
 * `Merchants.tsx`'s own `MerchantDetailPanel`) and kept only the
 * transactions list as a route of its own.
 *
 * A real `GET /merchants/{id}` rather than a lookup into the list cache
 * `CategoryDetail` uses: an id that names no merchant has to read as a
 * genuine failure through `<Async>`'s error branch (the brief's own test
 * finds `role="alert"` for `/merchants/nope`), and only a request that can
 * itself 404 produces that. The list cache also is not guaranteed to be
 * warm — a bookmarked or shared link is the direct-navigation case
 * categories does not have to answer to.
 *
 * `id: string | undefined` — `useParams()`'s own typing, unforced, the same
 * reasoning `CategoryDetail.tsx` gives: the router types a param as possibly
 * absent, and a generic that asserts it away just moves the lie. `enabled`
 * keeps the query parked rather than firing with an empty id; a route that
 * matched `MERCHANT_TRANSACTIONS_PATH` always has a non-empty `:id` in
 * practice, so this is a type-level guard rather than a reachable branch.
 */
export function useMerchant(id: string | undefined): AsyncState<Merchant> {
  const query = useQuery({
    queryKey: keys.merchants.detail(id ?? ''),
    queryFn: () => getMerchant(id as string),
    enabled: id !== undefined,
  })
  return fromQuery(query, {
    message: () => 'Merchant could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * A merchant id as something to name: still loading, found, gone, or unavailable (5xx or no response,
 * which says nothing about the merchant). A rule's "Set merchant" stores only the id, so its sentence is
 * built from this.
 *
 * Gone is a 4xx (no such merchant for this user, or not an id at all) OR a 200 with `is_active: false`.
 * Deleting a merchant is a soft delete, and `GET /merchants/{id}` still answers for it; but the rule
 * engine resolves `set_merchant` among ACTIVE merchants only and silently skips the action, and a rules
 * PATCH naming it is a 422. Naming it would show an action that does nothing (reviewer I1).
 */
export type MerchantLookup =
  | { status: 'loading' }
  | { status: 'found'; merchant: Merchant }
  | { status: 'unknown' }
  | { status: 'unavailable' }

/** A 4xx is the server's answer about the id; retrying it asks the same question again. */
const goneStatus = (e: unknown) => e instanceof MerchantsError && e.status >= 400 && e.status < 500

function lookupOf(q: UseQueryResult<Merchant>): MerchantLookup {
  if (q.data !== undefined) return q.data.is_active ? { status: 'found', merchant: q.data } : { status: 'unknown' }
  if (q.isError) return goneStatus(q.error) ? { status: 'unknown' } : { status: 'unavailable' }
  return { status: 'loading' }
}

/**
 * Names for a set of merchant ids, each read BY ID (`GET /merchants/{id}`), never looked up in a list page:
 * the user may have more merchants than any page holds, and a rule may name any of them. The key is
 * `useMerchant`'s, so a merchant read on either side is one cache entry. Empty ids are skipped.
 *
 * Returns `lookup(id)`; an id that was not asked for reads as loading.
 */
export function useMerchantNames(ids: readonly string[]): (id: string) => MerchantLookup {
  // A string key, so a fresh array of the same ids each render is the same query set.
  const key = [...new Set(ids.filter((id) => id !== ''))].sort().join('\n')
  const unique = useMemo(() => (key === '' ? [] : key.split('\n')), [key])
  // Plain objects, so TanStack's structural sharing keeps the record (and so `lookup`) the same object
  // until a name actually changes; a Map of query results would be new every render.
  const combine = useCallback(
    (results: UseQueryResult<Merchant>[]): Record<string, MerchantLookup> =>
      Object.fromEntries(unique.map((id, i) => [id, results[i] === undefined ? { status: 'loading' } : lookupOf(results[i])])),
    [unique],
  )
  const found = useQueries({
    queries: unique.map((id) => ({
      queryKey: keys.merchants.detail(id),
      queryFn: () => getMerchant(id),
      retry: (failures: number, e: unknown) => !goneStatus(e) && failures < 2,
    })),
    combine,
  })
  return useCallback(
    (id: string): MerchantLookup => found[id] ?? { status: 'loading' },
    [found],
  )
}

/**
 * The shape `MerchantTransactions.tsx` needs, not the wire's — matching
 * `MerchantPage`'s own reasoning just above. `amountCents` is the field that
 * actually differs in TYPE: the wire's `amount` is a decimal STRING, and
 * `parseMoney` runs HERE, once, at the API boundary — the same place
 * `parseMoney.ts`'s own docblock says it belongs, rather than in the
 * column's `render`, which would re-parse the same string on every render a
 * DataGrid row happens to take.
 *
 * `currency` travels alongside `amountCents` rather than being assumed —
 * `Money`'s own default (`'CAD'`) is a fallback for a caller with nothing
 * better, not a claim every transaction is denominated in it, and this row
 * always has `TransactionResponse.iso_currency_code` to hand instead.
 *
 * `merchantName` is `TransactionResponse.merchant_name` — the raw string the
 * bank or provider supplied, distinct from `name` (the transaction's OWN
 * description, which may be edited or normalised). Both are kept, because
 * the design wants both on screen: `name` bold as the row's primary line,
 * `merchantName` beneath it in a muted monospace face — see
 * `MerchantTransactions.tsx`'s own Description column.
 *
 * `categoryId`/`personalFinanceCategoryPrimary` are two different claims
 * about the SAME transaction, not a fallback pair resolved here: the first is
 * OUR category, resolvable against `useCategories()`'s own list; the second
 * is the upstream provider's own guess, which this app has never taken a
 * position on and is not about to invent one for now. `MerchantTransactions`
 * decides how to show either — this type only carries both through
 * unopinionated, the same as every other field above it.
 *
 * **`amountCents` is re-signed for display via `signedTransactionCents`**
 * (`../../api/parseMoney.ts` — a wire-format convention, not an accounts- or
 * merchants-module concern, so it lives beside `parseMoney` itself) —
 * storage is expense-positive, so a raw `parseMoney` on `t.amount` renders a
 * grocery bill `+$118.44` in `--pos` green, an expense colouring as a gain.
 * Shipped inverted since M11 (#439); `useAccountTransactions.ts` found and
 * fixed the identical bug first (#438) and this reuses its helper rather
 * than a second implementation — see that function's own docblock for the
 * full chain and for why `/transactions/summary`'s `net` is a DIFFERENT
 * field that must NOT be re-signed the same way.
 */
export type MerchantTransaction = {
  id: string
  name: string
  merchantName: string | null
  date: string
  amountCents: number
  currency: string
  isPending: boolean
  paymentChannel: string | null
  locationCity: string | null
  categoryId: string | null
  personalFinanceCategoryPrimary: string | null
}

export type MerchantTransactionPage = {
  transactions: MerchantTransaction[]
  total: number
}

async function listMerchantTransactions(merchantId: string): Promise<MerchantTransactionPage> {
  const { data, error, response } = await api.GET('/api/v1/transactions', {
    params: { query: { merchant_id: merchantId, page: 1, page_size: 25 } },
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return {
    transactions: data.transactions.map((t) => ({
      id: t.id,
      name: t.name,
      merchantName: t.merchant_name ?? null,
      date: t.transaction_date,
      amountCents: signedTransactionCents(parseMoney(t.amount)),
      currency: t.iso_currency_code,
      isPending: t.is_pending,
      paymentChannel: t.payment_channel ?? null,
      locationCity: t.location_city ?? null,
      categoryId: t.category_id ?? null,
      personalFinanceCategoryPrimary: t.personal_finance_category_primary ?? null,
    })),
    total: data.total,
  }
}

/**
 * The merchant's own transactions, scoped server-side by `merchant_id` —
 * originally Task 11, now backing `MerchantTransactions.tsx` at
 * `/merchants/:id/transactions` rather than a detail screen. No pager: the
 * brief asks for the list, not a paginated one, so `page_size` is fixed
 * rather than driven by a control nothing on this screen exposes yet.
 *
 * `MerchantsError` again rather than a new class — this fetch exists only in
 * service of that one route (M6's own Transactions module has not landed,
 * so there is no `TransactionsError` to reach for), and it throws from the
 * same one place every read and write in this file does.
 */
export function useMerchantTransactions(merchantId: string): AsyncState<MerchantTransactionPage> {
  const query = useQuery({
    queryKey: keys.transactions.list({ merchant_id: merchantId }),
    queryFn: () => listMerchantTransactions(merchantId),
  })
  return fromQuery(query, {
    message: () => 'Transactions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (page) => page.total === 0,
  })
}

/**
 * `POST /merchants` — Task 7.
 *
 * Invalidates `keys.merchants.all()`, **not** `.list(filters)` with whatever
 * the screen currently has in view. The narrower key was tried first in
 * `useCreateCategory`'s own history and rejected for the same reason it would
 * be wrong here: the merchant just created may not match the filter in view
 * (a new "Costco" created while the type filter is set to `person`).
 * Invalidating the resource ROOT clears every cached list page and every
 * cached detail at once, so neither can linger stale.
 *
 * Takes `MerchantCreate` directly, not a wrapper object — there is only ever
 * one shape of creation now that forking a global merchant is gone (product
 * decision 2026-08-21). The `{ body, forkedFromGlobal? }` shape existed
 * solely to carry that one bit past `onSuccess`; removing the feature removes
 * the reason for the wrapper.
 */
export function useCreateMerchant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: MerchantCreate) => {
      const { data, error, response } = await api.POST('/api/v1/merchants', { body })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.merchants.all() })
      // No `merchant_type` — `merchant` is an ENTITY_STEM and the entity
      // never takes an enum suffix; see `events.ts`'s M11 Merchants comment.
      emit('merchant_created')
    },
    // A 400 is the duplicate-name answer: the merchant exists, created since whatever search or list is
    // cached. Refresh them so it appears (and the picker's Create, which offers only when no exact match
    // is shown, withdraws) instead of offering the same doomed create for the cache's lifetime (reviewer M1).
    onError: (e) => {
      if (e instanceof MerchantsError && e.status === 400) void client.invalidateQueries({ queryKey: keys.merchants.all() })
    },
  })
}

/**
 * What an edit to an EXISTING merchant (rename, archive, delete, logo) makes stale: every merchant read,
 * and the picker's "Most used" tiles, which carry a merchant's name and logo and sit under their own root
 * (`keys.merchantUsage`). An open picker keeps its tiles until refetched, so left alone a deleted merchant
 * stays offered and picking it PATCHes a dead id; a rename shows the old name. Architect I1.
 * Creating a merchant does not need the second key: a new merchant has no usage to rank.
 */
function invalidateMerchantReads(client: QueryClient) {
  client.invalidateQueries({ queryKey: keys.merchants.all() })
  client.invalidateQueries({ queryKey: keys.merchantUsage.all() })
}

/**
 * `PATCH /merchants/{id}` — Task 8.
 *
 * **Corrected, final review C2: a write aimed at a global merchant is 403,
 * not 404.** `_get_merchant_with_access` (`merchants.py:376-393`) selects on
 * `user_id` FIRST — a miss there is not by itself conclusive — and only THEN
 * re-queries for a global row with that id; if one exists it raises 403
 * BEFORE the 404 is ever reached. The 404 that follows really does mean
 * "not found or not yours," on the path where no global row exists either.
 * This previously claimed the opposite — that a global write "falls out at
 * not-found" — which was backwards and left `MerchantForm.tsx` mapping 404 to
 * "cannot be edited" while 403 fell through to a generic toast. The caller is
 * what turns each status into copy; this hook only carries it through, the
 * same way `useUpdateCategory` does for the identical shape on categories.
 *
 * Same invalidation reasoning as `useCreateMerchant` just above: the resource
 * root, not the list filters in view — an edited merchant's new fields may
 * move it in or out of the filter currently on screen.
 */
export function useUpdateMerchant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: MerchantUpdate }) => {
      const { data, error, response } = await api.PATCH('/api/v1/merchants/{merchant_id}', {
        params: { path: { merchant_id: id } },
        body,
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data
    },
    onSuccess: () => {
      invalidateMerchantReads(client)
      // No properties, same reasoning as `useCreateMerchant` above — `body`
      // has no field this event may carry, and `body.type` specifically is
      // exactly the entity-plus-enum shape `events.ts`'s comment refuses.
      // Also fires for `archive()`'s own PATCH (`{ is_active: false }`) —
      // that is a real edit to the record and Task 12 registered no separate
      // name for it.
      emit('merchant_edited')
    },
  })
}

/**
 * `POST /merchants/{id}/logo`, `multipart/form-data` — Task 9.
 *
 * **The caller supplies the `FormData`; this hook never touches
 * `Content-Type`.** `openapi-fetch`'s own `coreFetch` skips its default
 * `application/json` header the moment the serialized body is a `FormData`
 * instance (verified against `openapi-fetch/src/index.js`) and leaves the
 * header to `Request`'s own constructor, which is what stamps the
 * `multipart/form-data; boundary=…` value the server needs to parse the
 * body. Setting it by hand here would fix the boundary at nothing and the
 * server could not split the parts.
 *
 * The field name is **`logo`** — `Body_upload_merchant_logo_…`'s one
 * required property, not a name this file invents.
 */
export function useUploadLogo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData()
      formData.append('logo', file)
      const { data, error, response } = await api.POST('/api/v1/merchants/{merchant_id}/logo', {
        params: { path: { merchant_id: id } },
        // `openapi-fetch`'s generated type for a multipart request body is the
        // `Body_…` schema shape, not `FormData` — the generator has no way to
        // know the handler wants a real `FormData` instance rather than an
        // object with a `logo` key, so the cast is the seam between the two.
        // The *runtime* value is a genuine `FormData`, which is what
        // `coreFetch` inspects to decide whether to skip the JSON header.
        body: formData as unknown as { logo: string },
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data
    },
    onSuccess: () => {
      invalidateMerchantReads(client)
      // No file name, size, or type — Task 12 registered `merchant_logo_-
      // uploaded` as a bare "this happened" event, the same shape
      // `password_reset_completed` uses, and none of `File`'s own fields
      // pass `assertKeyRegistered` today regardless.
      emit('merchant_logo_uploaded')
    },
  })
}

/**
 * `DELETE /merchants/{id}/logo` — Task 9's other half. A 204, so — same note
 * `useDeleteMerchant` gives below — no `data` check.
 *
 * **No analytics here.** Task 12's brief named exactly five events and
 * `merchant_logo_uploaded` is the only one of the logo pair — a removal name
 * was never registered, and adding one unasked is the same unreviewed
 * addition `vocabulary.ts`'s own docblock argues a privacy guard cannot
 * afford. See `events.ts`'s M11 Merchants comment.
 */
export function useDeleteLogo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE('/api/v1/merchants/{merchant_id}/logo', {
        params: { path: { merchant_id: id } },
      })
      if (error !== undefined) throw new MerchantsError(error, response.status)
    },
    onSuccess: () => invalidateMerchantReads(client),
  })
}

/**
 * `DELETE /merchants/{id}` — Task 10, a **soft** delete: `delete_merchant`'s
 * own description says the row survives and every transaction that pointed
 * at it has `merchant_id` cleared "via ON DELETE SET NULL." That sentence,
 * read directly out of `openapi.json` rather than typed by hand here, is what
 * the confirmation copy in `Merchants.tsx` is built against — see the
 * docblock there for why a live probe could not be run this round and why the
 * schema's own wording stands in for it.
 *
 * No `data` check on the response, unlike `useCreateMerchant` and
 * `useUpdateMerchant` above: a 204 carries no body, so `data === undefined`
 * is the SUCCESS case here — the same asymmetry `useDeleteCategory` documents
 * for the identical shape.
 */
export function useDeleteMerchant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE('/api/v1/merchants/{merchant_id}', {
        params: { path: { merchant_id: id } },
      })
      if (error !== undefined) throw new MerchantsError(error, response.status)
    },
    onSuccess: () => {
      invalidateMerchantReads(client)
      // No properties — the id being deleted is exactly the thing this must
      // not carry, and `merchant_deleted`'s own name is the whole event.
      emit('merchant_deleted')
    },
  })
}
